import { memo } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { IconXmarkOutline18 as XmarkIcon } from "nucleo-ui-outline-18"

import { isRecentlyBack } from "@productlane/lib/src/out-of-office"
import { Button, IconButton, Tooltip } from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"

/** After a return, offers back the threads the cover still holds. */
export const OutOfOfficeReturnBanner = memo(function OutOfOfficeReturnBanner() {
	const [user] = useQuery(queries.currentUser())
	const membership = user?.memberships
	// The server query only runs for someone back within the last week.
	const recentlyBack =
		!!membership?.outOfOfficeCoverId &&
		isRecentlyBack(membership.outOfOfficeUntil)

	const utils = api.useUtils()
	const offer = api.outOfOffice.return.useQuery(undefined, {
		enabled: recentlyBack,
	})
	const takeBack = api.outOfOffice.takeBack.useMutation()
	const dismiss = api.outOfOffice.dismissReturn.useMutation()

	if (!recentlyBack || !offer.data) return null

	const settle = async (action: "take-back" | "leave") => {
		try {
			if (action === "take-back") await takeBack.mutateAsync()
			else await dismiss.mutateAsync()
			utils.outOfOffice.return.setData(undefined, null)
			analytics.track("out-of-office-return", { action })
		} catch {
			toast.error({
				title:
					action === "take-back"
						? "Failed to take back your threads"
						: "Failed to dismiss",
			})
		}
	}

	return (
		<OutOfOfficeReturnCard
			coverFirstName={
				offer.data.coverName.split(" ")[0] ?? offer.data.coverName
			}
			threadCount={offer.data.threadCount}
			isTakingBack={takeBack.isLoading}
			isBusy={takeBack.isLoading || dismiss.isLoading}
			onTakeBack={() => void settle("take-back")}
			onLeave={() => void settle("leave")}
		/>
	)
})

export function OutOfOfficeReturnCard({
	coverFirstName,
	threadCount,
	isTakingBack = false,
	isBusy = false,
	onTakeBack,
	onLeave,
}: {
	coverFirstName: string
	threadCount: number
	isTakingBack?: boolean
	isBusy?: boolean
	onTakeBack: () => void
	onLeave: () => void
}) {
	// Closing it is the same answer as leaving them: the threads stay put.
	const leaveLabel = `Leave with ${coverFirstName}`

	return (
		<div className="mx-1.5 mb-2 flex flex-col gap-2.5 rounded-lg bg-white p-3 shadow-md ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
			<div className="flex items-start gap-2">
				<span className="min-w-0 flex-1 text-xs text-primary">
					Welcome back. {coverFirstName} still has {threadCount} of your threads
					open.
				</span>
				<Tooltip label={leaveLabel}>
					<IconButton
						variant="ghost"
						size="sm"
						Icon={XmarkIcon}
						aria-label={leaveLabel}
						className="-mr-1.5 -mt-1 flex-none"
						disabled={isBusy}
						onClick={onLeave}
					/>
				</Tooltip>
			</div>
			<Button
				type="button"
				size="sm"
				className="self-start"
				isLoading={isTakingBack}
				disabled={isBusy}
				onClick={onTakeBack}
			>
				Take them back
			</Button>
		</div>
	)
}
